import produce from 'immer';
import { startCase } from 'lodash';
import { from } from 'rxjs';

import { request } from '@swf/core/api/request';
import { MoneyOption } from '@swf/core/enums';
import type { RootState } from '@swf/core/store';
import { dispatch, getState, select } from '@swf/core/store';
import type {
  CasinoGame,
  CasinoGameInstance,
  CasinoInterruptedGame,
  CasinoTable,
  Page,
  StreamData
} from '@swf/core/types/CasinoGame';
import { PlayType } from '@swf/core/types/CasinoGame';
import { RaceType, Status } from '@swf/core/types/CasinoRaces';
import type { InstantBonus } from '@swf/core/types/InstantBonus';
import type { StarsCoinReward } from '@swf/core/types/StarsCoinReward';
import { RewardType } from '@swf/core/types/StarsCoinReward';
import { isIPadOS } from '@swf/core/utils';
import { menus } from '@swf/core/utils/casinoFilters';
import type { CasinoQuery } from '@swf/generated/types/common/Casino';
import { CasinoJoinOperand, CasinoOperand } from '@swf/generated/types/common/Casino';
import type { CasinoServiceParameters } from '@swf/generated/types/common/CasinoParams';

import type { CasinoBonusGame } from '../types/CasinoBonus';
import type { WidgetReducer } from '../types/Widget';

interface SearchParams {
  query: string;
  confirmed?: boolean;
  paramCode?: string;
  gip?: boolean;
}

export const hasAffordable = ({
  rewards,
  balance
}: {
  rewards: StarsCoinReward[];
  balance: number;
}) => rewards.filter(({ price }) => balance >= price).length;

export const isActive = (ib: InstantBonus) => ib.status === 10;

const sortIBScore = (bonus: InstantBonus) => (!isActive(bonus) ? 10 : bonus.paused ? 5 : 0);

export const sortIB = (a: InstantBonus, b: InstantBonus) => sortIBScore(a) - sortIBScore(b);

export const isInstantBonus = (type?: RewardType) =>
  type && [RewardType.CasinoInstant, RewardType.VegasInstant].includes(type);

export const isFSB = (type?: RewardType) =>
  type && [RewardType.CasinoFSB, RewardType.VegasFSB].includes(type);

export const isRunning = (status: Status) => [Status.STARTED, Status.REGCLOSED].includes(status);

export const isCompleted = (status: Status) =>
  [Status.COMPLETED, Status.CANCELLED, Status.ABORTED].includes(status);

export const isLeaderboard = (status: Status) => isRunning(status) || isCompleted(status);

export const isFinished = (status: Status, noStanding: boolean) =>
  [Status.COMPLETED, Status.CANCELLED].includes(status) ||
  (noStanding && status === Status.REGCLOSED);

export const buildPayload = ({
  showVegasContent,
  activeFilter,
  pg = 0,
  filters,
  siteName,
  defaultParams
}: {
  showVegasContent: boolean;
  activeFilter: number;
  pg?: number;
  filters: string;
  siteName: string;
  defaultParams: CasinoServiceParameters;
}) => {
  const filterType = menus[filters] || {};
  const filter = filterType[activeFilter];
  let params: CasinoServiceParameters = { ...defaultParams };
  if (filter) {
    params = filter.makeQuery(params, siteName);
  }
  return { meta: { size: params.psz }, params: { ...params, pg } };
};

export const gamesPerColumn = (mosaic: string) => {
  switch (mosaic) {
    case '4 squares':
      return 4;
    case '1 top 1 bottom':
      return 2;
    case '1 big square':
      return 1;
    default:
      return 3;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const gameList = (data?: any): CasinoGame[] =>
  data?.length ? (data as CasinoGame[]) : data?.games ? data.games : ([] as CasinoGame[]);

export const asPage = (data?: StreamData | CasinoGame[]): Page => ({
  games: gameList(data),
  complete: true,
  page: 0
});

export const getWindow = ({
  table,
  playType = PlayType.Single,
  rootState
}: {
  table: CasinoTable | CasinoInterruptedGame | CasinoGameInstance | CasinoBonusGame;
  playType?: PlayType;
  rootState: RootState;
}): Window | null => {
  const isMobile = select.device.isMobile(rootState);
  const isEmbedded = select.site.isEmbedded(rootState);

  if (isEmbedded) {
    return null;
  } else if (isMobile || isIPadOS) {
    // to handle desktop mode on iPadOS
    return window;
  }
  return window.open(
    '',
    `${
      (table as CasinoInterruptedGame).tId ||
      (table as CasinoTable).id ||
      (table as CasinoBonusGame).bonusId
    }-${playType}`,
    'width=960,height=540,resizable=1'
  );
};

export const isRedirected = (gameWindow: Window) => {
  try {
    return gameWindow.location.href !== 'about:blank';
  } catch (i) {
    return true;
  }
};

export const closeWindow = (gameWindow: Window | null | undefined, isMobile: boolean) => {
  if (gameWindow && !isMobile) {
    gameWindow.close();
  }
};

export const searchRequest = async (
  params: Pick<SearchParams, 'paramCode' | 'query' | 'gip'> | CasinoQuery[],
  rootState: RootState,
  options: { gip: boolean } = { gip: false }
): Promise<CasinoGame[]> => {
  const siteName = select.site.getName(rootState);
  // TODO: check if still required and remove
  const moneyOption = {
    formalparamcode: 'moneyoption',
    values: [select.currency.isPlayMoney(rootState) ? MoneyOption.Play : MoneyOption.Real],
    operand: CasinoOperand.EQ,
    join: CasinoJoinOperand.AND,
    dynamic: false
  };
  let qry;
  if (Array.isArray(params)) {
    qry = [...params, moneyOption];
  } else {
    qry = [
      {
        formalparamcode: params.paramCode || 'search_text',
        values: [params.query],
        operand: CasinoOperand.EQ,
        join: CasinoJoinOperand.AND,
        dynamic: false
      },
      moneyOption
    ];
  }

  const { body } = await request({
    service: 'casino.games.all',
    ...select.site.getRequestOptions(rootState),
    body: {
      gip: options.gip || (params as SearchParams).gip,
      pg: 0,
      qry,
      order: [{ column: 'numOf', sort: 1 }],
      grp: true,
      ims: { tags: ['icnSquare'] },
      psz: 30,
      tgs: true,
      includeTags: [`Exclusive ${siteName}`, `New ${siteName}`, `Hot ${siteName}`, `High Roller`]
    }
  });
  return body;
};

export const search = (query: CasinoQuery[], rootState: RootState) =>
  from(searchRequest(query, rootState));

export const streamUpdater =
  <T>(
    producer: (game: CasinoGame, data: T) => void
  ): WidgetReducer<unknown, { games: StreamData } | undefined, T[]> =>
  (state, { data }) =>
    produce(state, (draft) => {
      draft?.games.games.forEach((game) => {
        game.isFavorite = false;
        game.fsb = false;
        data.forEach((item) => producer(game, item));
      });
    });

export const launchGame = ({
  id = '',
  code,
  raceId,
  race,
  fsb,
  externalLaunch
}: {
  id?: string;
  code?: string;
  raceId?: string;
  race?: boolean;
  fsb?: string;
  externalLaunch?: boolean;
}) => {
  const fsbLaunch = !!fsb;
  const join = { operand: CasinoOperand.EQ, join: CasinoJoinOperand.AND, dynamic: false };
  let gameQuery: CasinoQuery[] = [];

  if (id) {
    gameQuery = [{ values: [startCase(id)], formalparamcode: 'search_text', ...join }];
  }

  if (code) {
    const [gameType, variantType] = code.split('-');
    gameQuery = [
      { values: [parseInt(gameType, 10)], formalparamcode: 'gametype', ...join },
      { values: [parseInt(variantType, 10)], formalparamcode: 'varianttype', ...join }
    ];
  }
  search(gameQuery, getState()).subscribe(([game]) => {
    const table = game?.tables[0];
    if ((race || raceId) && table) {
      dispatch.casino.race({
        game,
        raceId: `${raceId || id}`,
        raceTypes: [RaceType.FEATURED],
        externalLaunch
      });
    } else if (table) {
      dispatch.casino.launch({ game, table, fsbLaunch, externalLaunch });
    }
  });
};
